import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";

function Shell({ children, testId }) {
  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4" data-testid={testId}>
      <div className="w-full max-w-md rounded-md border border-[#2A2E37] bg-[#121418] p-10 text-center rise-in">{children}</div>
    </div>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [kind, setKind] = useState("subscription");
  const [creator] = useState(() => localStorage.getItem("velvet_pending_creator"));

  useEffect(() => {
    if (!sessionId) return setState("error");
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { localStorage.removeItem("velvet_pending_creator"); setKind(data.kind); return setState("paid"); }
        if (["expired", "failed"].includes(data.payment_status)) return setState("error");
      } catch { /* retry */ }
      if (++tries >= 10) return setState("timeout");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <Shell testId="payment-success-page">
      {state === "checking" && (<><Loader2 className="mx-auto h-10 w-10 animate-spin text-[#FF2E4D]" /><p className="mt-4 text-gray-300">Confirming your payment...</p></>)}
      {state === "paid" && (
        <>
          <CheckCircle2 className="mx-auto h-14 w-14 text-gray-300" />
          <h1 className="mt-4 font-display text-xl font-semibold" data-testid="payment-success-title">{kind === "tip" ? "Tip sent." : kind === "unlock" ? "Unlocked." : "You're in."}</h1>
          <p className="mt-2 text-sm text-gray-400">
            {kind === "unlock" ? "Your message is ready to view." : kind === "tip" ? `Thanks for supporting ${creator ? `@${creator}` : "this creator"}. Your note landed in their inbox.` : "Your subscription is active. Everything is unlocked."}
          </p>
          <Link to={creator ? (kind === "unlock" ? `/messages/${creator}` : `/u/${creator}`) : "/"} data-testid="payment-go-creator-btn" className="btn-neon mt-6 inline-block rounded-sm px-6 py-3 font-semibold text-white">
            {creator ? (kind === "unlock" ? "Open chat" : `Go to @${creator}`) : "Back to feed"}
          </Link>
        </>
      )}
      {(state === "error" || state === "timeout") && (
        <>
          <XCircle className="mx-auto h-14 w-14 text-[#FF2E4D]" />
          <h1 className="mt-4 font-display text-xl font-semibold" data-testid="payment-error-title">{state === "timeout" ? "Still processing" : "Payment not completed"}</h1>
          <p className="mt-2 text-sm text-gray-400">{state === "timeout" ? "Refresh this page in a moment." : "No charge was made."}</p>
          <Link to="/" className="mt-6 inline-block text-sm text-[#FF2E4D] hover:underline" data-testid="payment-back-btn">Back to feed</Link>
        </>
      )}
    </Shell>
  );
}

export function PaymentCancel() {
  const creator = localStorage.getItem("velvet_pending_creator");
  return (
    <Shell testId="payment-cancel-page">
      <XCircle className="mx-auto h-14 w-14 text-gray-500" />
      <h1 className="mt-4 font-display text-xl font-semibold">Checkout canceled</h1>
      <p className="mt-2 text-sm text-gray-400">No worries — you can subscribe any time.</p>
      <Link to={creator ? `/u/${creator}` : "/"} className="mt-6 inline-block text-sm text-[#FF2E4D] hover:underline" data-testid="payment-cancel-back-btn">Go back</Link>
    </Shell>
  );
}
